import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { TrendingUp, TrendingDown, RefreshCw, CheckCheck, X, Check, Settings2, Lock, Unlock, Plus, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  PageHeader,
  Card,
  StatusBadge,
  Input,
  Modal,
  Select,
  type ColumnDef,
} from '@/components/ui'
import { useToast } from '@/components/ui'
import { formatCurrency } from '../shared.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PendingAdjustment {
  id: string
  articleId: string
  itemCode: string
  itemName: string
  groupName: string | null
  oldPurchasePriceCents: number
  newPurchasePriceCents: number
  oldSalesPriceCents: number
  suggestedSalesPriceCents: number
  markupPct: number
  deltaEkCents: number
  deltaVkCents: number
  priceLocked: boolean
  createdAt: string
}

interface PriceTier {
  id: string
  groupId: string | null
  groupName: string | null
  ekFromCents: number
  ekToCents: number | null
  markupPct: number
  sortOrder: number
}

interface ArticleGroup {
  id: string
  name: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function DeltaBadge({ cents }: { cents: number }) {
  if (cents === 0) return <StatusBadge status="neutral" label="±0" />
  const formatted = (cents > 0 ? '+' : '') + formatCurrency(cents / 100)
  return <StatusBadge status={cents > 0 ? 'success' : 'warning'} label={formatted} />
}

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function groupTiersByScope(tiers: PriceTier[]): { label: string; items: PriceTier[] }[] {
  const global = tiers.filter((t) => t.groupId === null)
  const byGroup = new Map<string, { label: string; items: PriceTier[] }>()
  for (const t of tiers.filter((t) => t.groupId !== null)) {
    const key = t.groupId!
    if (!byGroup.has(key)) byGroup.set(key, { label: t.groupName ?? key, items: [] })
    byGroup.get(key)!.items.push(t)
  }
  const result: { label: string; items: PriceTier[] }[] = []
  if (global.length > 0) result.push({ label: 'Global (Fallback)', items: global })
  for (const group of byGroup.values()) result.push(group)
  return result
}

// ─── Tiers Modal ──────────────────────────────────────────────────────────────

function TiersModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: tiers = [], isLoading: tiersLoading } = useQuery<PriceTier[]>({
    queryKey: ['price-tiers'],
    queryFn: () => apiClient.get('/articles/price-tiers').then((r) => r.data),
  })

  const { data: groups = [] } = useQuery<ArticleGroup[]>({
    queryKey: ['article-groups'],
    queryFn: () => apiClient.get('/articles/groups').then((r) => r.data),
  })

  // Form state for new tier
  const [newGroupId, setNewGroupId] = useState<string>('__global__')
  const [newEkFrom, setNewEkFrom] = useState('0')
  const [newEkTo, setNewEkTo] = useState('')
  const [newMarkup, setNewMarkup] = useState('300')

  const saveMutation = useMutation({
    mutationFn: () => {
      const ekFrom = Math.round(parseFloat(newEkFrom || '0') * 100)
      const ekTo = newEkTo.trim() !== '' ? Math.round(parseFloat(newEkTo) * 100) : null
      const markupPct = parseFloat(newMarkup)
      return apiClient.put('/articles/price-tiers', {
        groupId: newGroupId === '__global__' ? null : newGroupId,
        ekFrom,
        ekTo,
        markupPct,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-tiers'] })
      setNewEkFrom('0')
      setNewEkTo('')
      setNewMarkup('300')
      toast({ title: 'Staffel gespeichert', tone: 'success' })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/articles/price-tiers/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['price-tiers'] }),
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const grouped = groupTiersByScope(tiers)

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Staffelaufschläge"
      description="Aufschlag auf den Einkaufspreis pro Preisbereich. Gruppenregeln überschreiben die globalen vollständig. Formel: VK = EK × (1 + Aufschlag / 100)"
      size="lg"
      footer={<Button variant="ghost" onClick={onClose}>Schließen</Button>}
    >
      {tiersLoading ? (
        <LoadingState label="Staffeln werden geladen…" />
      ) : (
        <div className="space-y-5">
          {/* Bestehende Staffeln */}
          {grouped.length === 0 ? (
            <p className="text-sm text-text-secondary">Noch keine Staffeln konfiguriert — Fallback ist 30% Aufschlag.</p>
          ) : (
            grouped.map((scope) => (
              <div key={scope.label}>
                <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-1.5">{scope.label}</p>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-subtle">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-text-secondary">EK von</th>
                        <th className="px-3 py-2 text-left font-medium text-text-secondary">EK bis</th>
                        <th className="px-3 py-2 text-left font-medium text-text-secondary">Aufschlag</th>
                        <th className="px-3 py-2 text-left font-medium text-text-secondary">Beispiel (EK Mitte)</th>
                        <th className="px-3 py-2 w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {scope.items.map((tier) => {
                        const midEk = tier.ekToCents != null
                          ? (tier.ekFromCents + tier.ekToCents) / 2
                          : tier.ekFromCents * 1.5 || 100
                        const exampleVk = midEk * (1 + tier.markupPct / 100)
                        return (
                          <tr key={tier.id} className="hover:bg-surface-subtle/50">
                            <td className="px-3 py-2 tabular-nums">{formatEuro(tier.ekFromCents)} €</td>
                            <td className="px-3 py-2 tabular-nums text-text-secondary">
                              {tier.ekToCents != null ? `${formatEuro(tier.ekToCents)} €` : '∞'}
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-semibold text-brand-600">{tier.markupPct.toLocaleString('de-DE')} %</span>
                            </td>
                            <td className="px-3 py-2 text-text-secondary tabular-nums text-xs">
                              EK {formatEuro(midEk)} € → VK {formatEuro(Math.round(exampleVk))} €
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                variant="ghost"
                                size="xs"
                                onClick={() => deleteMutation.mutate(tier.id)}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 size={12} />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}

          {/* Neue Staffel */}
          <div className="border-t border-border pt-4 space-y-3">
            <p className="text-sm font-medium text-text-primary">Neue Staffel</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Select
                label="Gruppe"
                value={newGroupId}
                onValueChange={(v) => setNewGroupId(v)}
                options={[
                  { value: '__global__', label: 'Global' },
                  ...groups.map((g) => ({ value: g.id, label: g.name })),
                ]}
              />
              <Input
                label="EK ab (€)"
                type="number"
                min={0}
                step={0.01}
                value={newEkFrom}
                onChange={(e) => setNewEkFrom(e.target.value)}
                hint="inkl."
              />
              <Input
                label="EK bis (€)"
                type="number"
                min={0}
                step={0.01}
                value={newEkTo}
                onChange={(e) => setNewEkTo(e.target.value)}
                hint="exkl. — leer = kein Limit"
              />
              <Input
                label="Aufschlag (%)"
                type="number"
                min={0}
                step={0.5}
                value={newMarkup}
                onChange={(e) => setNewMarkup(e.target.value)}
                hint={`VK = EK × ${(1 + parseFloat(newMarkup || '0') / 100).toFixed(2)}`}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              <Plus size={14} />
              Staffel hinzufügen
            </Button>
          </div>

          {/* Hinweis */}
          <div className="rounded-lg bg-surface-subtle p-3 text-xs text-text-secondary space-y-1">
            <p><strong>Tipp:</strong> Lege mehrere Staffeln pro Gruppe an, z.B.:</p>
            <p>Global: 0–10 € → 1000 % | 10–20 € → 700 % | 20–50 € → 500 % | 50+ € → 300 %</p>
            <p>Gruppe "Kabel": 0–20 € → 2000 % (überschreibt Global komplett für diese Gruppe)</p>
            <p>Artikel mit gesetzter <strong>Preissperre</strong> werden niemals automatisch angepasst.</p>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ─── Columns ─────────────────────────────────────────────────────────────────

function buildColumns(
  onApprove: (id: string) => void,
  onReject: (id: string) => void,
  onToggleLock: (articleId: string, locked: boolean) => void,
  isPending: boolean,
): ColumnDef<PendingAdjustment>[] {
  return [
    {
      accessorKey: 'itemCode',
      header: 'Artikelnr.',
      size: 110,
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-text-secondary">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'itemName',
      header: 'Bezeichnung',
      cell: ({ row }) => (
        <span className={`text-sm font-medium line-clamp-2 ${row.original.priceLocked ? 'text-text-secondary line-through' : ''}`}>
          {row.original.itemName}
        </span>
      ),
    },
    {
      accessorKey: 'groupName',
      header: 'Gruppe',
      size: 120,
      cell: ({ getValue }) => (
        <span className="text-sm text-text-secondary">{(getValue() as string | null) ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'newPurchasePriceCents',
      header: 'EK',
      size: 90,
      cell: ({ getValue }) => (
        <span className="text-sm tabular-nums">{formatCurrency((getValue() as number) / 100)}</span>
      ),
    },
    {
      accessorKey: 'markupPct',
      header: 'Aufschlag',
      size: 90,
      cell: ({ getValue }) => (
        <span className="text-sm tabular-nums text-text-secondary">{(getValue() as number).toLocaleString('de-DE')} %</span>
      ),
    },
    {
      accessorKey: 'oldSalesPriceCents',
      header: 'VK aktuell',
      size: 100,
      cell: ({ getValue }) => (
        <span className="text-sm tabular-nums">{formatCurrency((getValue() as number) / 100)}</span>
      ),
    },
    {
      accessorKey: 'suggestedSalesPriceCents',
      header: 'VK vorgeschlagen',
      size: 130,
      cell: ({ row }) => row.original.priceLocked ? (
        <span className="flex items-center gap-1 text-xs text-text-secondary"><Lock size={11} /> Gesperrt</span>
      ) : (
        <span className="text-sm tabular-nums font-semibold text-brand-600">
          {formatCurrency(row.original.suggestedSalesPriceCents / 100)}
        </span>
      ),
    },
    {
      accessorKey: 'deltaVkCents',
      header: 'Δ VK',
      size: 100,
      cell: ({ row }) => row.original.priceLocked ? null : <DeltaBadge cents={row.original.deltaVkCents} />,
    },
    {
      id: 'actions',
      header: '',
      size: 130,
      cell: ({ row }) => (
        <div className="flex gap-1.5 items-center">
          {!row.original.priceLocked && (
            <>
              <Button
                variant="primary"
                size="xs"
                onClick={() => onApprove(row.original.id)}
                disabled={isPending}
                title="Freigeben"
              >
                <Check size={12} />
              </Button>
              <Button
                variant="danger"
                size="xs"
                onClick={() => onReject(row.original.id)}
                disabled={isPending}
                title="Ablehnen"
              >
                <X size={12} />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="xs"
            onClick={() => onToggleLock(row.original.articleId, !row.original.priceLocked)}
            disabled={isPending}
            title={row.original.priceLocked ? 'Preissperre aufheben' : 'Preis sperren'}
          >
            {row.original.priceLocked ? <Unlock size={12} /> : <Lock size={12} />}
          </Button>
        </div>
      ),
    },
  ]
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function PriceAdjustmentSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showTiers, setShowTiers] = useState(false)

  const { data: pending = [], isLoading, error } = useQuery<PendingAdjustment[]>({
    queryKey: ['price-adjustments-pending'],
    queryFn: () => apiClient.get('/articles/price-adjustments/pending').then((r) => r.data),
    staleTime: 60_000,
  })

  const { mutate: runCheck, isPending: checking } = useMutation({
    mutationFn: () => apiClient.post('/articles/price-adjustments/check', {}),
    onSuccess: (res) => {
      const d = res.data as { created: number; skipped: number; locked: number }
      toast({
        title: 'Prüfung abgeschlossen',
        description: `${d.created} neue Vorschläge, ${d.skipped} unverändert${d.locked > 0 ? `, ${d.locked} gesperrt` : ''}`,
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['price-adjustments-pending'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: approveAll, isPending: approvingAll } = useMutation({
    mutationFn: () => apiClient.post('/articles/price-adjustments/approve-all', {}),
    onSuccess: (res) => {
      const d = res.data as { approved: number; errors: number }
      toast({
        title: 'Alle freigegeben',
        description: `${d.approved} Preise aktualisiert${d.errors > 0 ? `, ${d.errors} Fehler` : ''}`,
        tone: d.errors > 0 ? 'warning' : 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['price-adjustments-pending'] })
      void queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: approveOne, isPending: approvingOne } = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/price-adjustments/${id}/approve`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-adjustments-pending'] })
      void queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: rejectOne, isPending: rejectingOne } = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/price-adjustments/${id}/reject`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['price-adjustments-pending'] }),
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const { mutate: toggleLock, isPending: togglingLock } = useMutation({
    mutationFn: ({ articleId, locked }: { articleId: string; locked: boolean }) =>
      apiClient.patch(`/articles/${articleId}/price-lock`, { locked }),
    onSuccess: (_res, vars) => {
      toast({
        title: vars.locked ? 'Preis gesperrt' : 'Preissperre aufgehoben',
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['price-adjustments-pending'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const activeCount = pending.filter((p) => !p.priceLocked).length
  const lockedCount = pending.filter((p) => p.priceLocked).length
  const totalDeltaUp = pending.filter((p) => !p.priceLocked && p.deltaVkCents > 0).length
  const totalDeltaDown = pending.filter((p) => !p.priceLocked && p.deltaVkCents < 0).length
  const isBusy = approvingOne || rejectingOne || approvingAll || togglingLock

  const columns = buildColumns(
    (id) => approveOne(id),
    (id) => rejectOne(id),
    (articleId, locked) => toggleLock({ articleId, locked }),
    isBusy,
  )

  if (isLoading) return <LoadingState label="Preisanpassungen werden geladen…" />
  if (error) return <ErrorState title="Fehler" description={(error as Error).message} />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Automatische Preisanpassung"
        description="Vorgeschlagene VK-Preise basierend auf EGIS-Einkaufspreisen und konfigurierten Staffelaufschlägen."
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowTiers(true)}>
              <Settings2 size={14} />
              Staffelaufschläge
            </Button>
            <Button variant="secondary" size="sm" onClick={() => runCheck()} disabled={checking}>
              <RefreshCw size={14} className={checking ? 'animate-spin' : ''} />
              {checking ? 'Wird geprüft…' : 'Jetzt prüfen'}
            </Button>
            {activeCount > 0 && (
              <Button variant="primary" size="sm" onClick={() => approveAll()} disabled={approvingAll}>
                <CheckCheck size={14} />
                {approvingAll ? 'Wird freigegeben…' : `Alle freigeben (${activeCount})`}
              </Button>
            )}
          </div>
        }
      />

      {/* Summary */}
      {pending.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          <Card>
            <Card.Body className="flex items-center gap-3 py-3">
              <span className="text-2xl font-bold tabular-nums text-text-primary">{activeCount}</span>
              <span className="text-sm text-text-secondary">ausstehend</span>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body className="flex items-center gap-3 py-3">
              <TrendingUp size={18} className="text-success-600" />
              <span className="text-lg font-semibold tabular-nums text-success-700">{totalDeltaUp}</span>
              <span className="text-sm text-text-secondary">Erhöhungen</span>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body className="flex items-center gap-3 py-3">
              <TrendingDown size={18} className="text-warning-600" />
              <span className="text-lg font-semibold tabular-nums text-warning-700">{totalDeltaDown}</span>
              <span className="text-sm text-text-secondary">Senkungen</span>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body className="flex items-center gap-3 py-3">
              <Lock size={18} className="text-text-secondary" />
              <span className="text-lg font-semibold tabular-nums text-text-secondary">{lockedCount}</span>
              <span className="text-sm text-text-secondary">gesperrt</span>
            </Card.Body>
          </Card>
        </div>
      )}

      {pending.length === 0 ? (
        <EmptyState
          title="Keine ausstehenden Anpassungen"
          description='Alle Preise entsprechen den Staffelregeln. Klicke auf „Jetzt prüfen" um eine neue Prüfung anzustoßen.'
        />
      ) : (
        <DataTable data={pending} columns={columns} />
      )}

      {showTiers && <TiersModal onClose={() => setShowTiers(false)} />}
    </div>
  )
}
